// Detalhes.jsx
// ------------------------------------------------------------
// Página responsável por exibir os detalhes completos de um projeto.
//
// Funções principais:
// ✔ Buscar o projeto pelo ID da rota
// ✔ Renderizar mídia dinâmica (imagem, vídeo ou YouTube)
// ✔ Exibir informações completas (título, descrição, tags)
// ✔ Exibir links de disponibilidade (com ícones)
// ✔ Mostrar projetos relacionados
// ✔ Controlar o comportamento do carrossel horizontal
// ✔ Garantir melhor experiência de navegação (scroll automático)
//
// Padrões adotados:
// ✔ Hooks sempre no topo
// ✔ Separação clara entre dados, lógica e renderização
// ✔ Interface controlada por dados (JSON)
// ✔ Código defensivo para evitar erros com dados ausentes
// ------------------------------------------------------------

import { React, useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Hooks do React Router
import projetosData from "../data/projetosGerais.json"; // Base de dados dos projetos
import Card from "../components/Card/Card"; // Card reutilizável
import iconesDisponibilidade from "../img/configuracaoDisponibilidade"; // Mapeamento de ícones por tipo

function Detalhes() {

  // ------------------------------------------------------------
  // 🔹 HOOKS (sempre no topo do componente)
  // ------------------------------------------------------------

  // Referência para o container do carrossel (scroll horizontal)
  const containerRef = useRef(null);

  // Estados que controlam a visibilidade das setas do carrossel
  const [podeIrEsquerda, setPodeIrEsquerda] = useState(false);
  const [podeIrDireita, setPodeIrDireita] = useState(true);

  // Captura o ID da URL (/projeto/:id)
  const { id } = useParams();

  // Hook de navegação
  const navigate = useNavigate();

  // ------------------------------------------------------------
  // 🔹 EFEITO: RESET DE SCROLL
  // ------------------------------------------------------------
  // Sempre que o ID muda (troca de projeto),
  // a página volta suavemente para o topo.
  //
  // Isso melhora a UX, pois o React Router não recarrega a página,
  // então o scroll anterior seria mantido.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, [id]);

  // ------------------------------------------------------------
  // 🔹 EFEITO: CONTROLE DO SCROLL DO CARROSSEL
  // ------------------------------------------------------------
  // Adiciona listeners para:
  // ✔ scroll do container
  // ✔ resize da janela
  //
  // Isso permite atualizar dinamicamente
  // a visibilidade das setas (esquerda/direita)
  useEffect(function () {
    const container = containerRef.current;

    if (!container) return;

    function atualizar() {
      verificarScroll();
    }

    // Verifica estado inicial
    verificarScroll();

    container.addEventListener("scroll", atualizar);
    window.addEventListener("resize", atualizar);

    // Cleanup para evitar vazamento de memória
    return function () {
      container.removeEventListener("scroll", atualizar);
      window.removeEventListener("resize", atualizar);
    };
  }, [id]);

  // ------------------------------------------------------------
  // 🔹 DADOS DO PROJETO
  // ------------------------------------------------------------

  // Converte ID da URL para número
  const projetoId = Number(id);

  // Busca o projeto correspondente no JSON
  const projeto = projetosData.find(function (p) {
    return p.id === projetoId;
  });

  // ------------------------------------------------------------
  // 🔴 VALIDAÇÃO DE SEGURANÇA
  // ------------------------------------------------------------
  // Caso o projeto não exista, evita erro na aplicação
  if (!projeto) {
    return (
      <div style={{ padding: 24 }}>
        <p>Projeto não encontrado.</p>
        <button onClick={() => navigate(-1)}>Voltar</button>
      </div>
    );
  }

  // ------------------------------------------------------------
  // 🔹 LÓGICA DERIVADA DO PROJETO
  // ------------------------------------------------------------

  const { apresentacao } = projeto;

  // Define a "tag principal" (primeira do array)
  // usada para encontrar projetos relacionados
  const mainTag = projeto.tags && projeto.tags[0];

  // Filtra projetos relacionados com base na tag principal
  const relacionado = projetosData
  .filter(function (p) {

    if (p.id === projetoId) return false;
    if (!p.tags || !projeto.tags) return false;

    // 🔥 Verifica se existe PELO MENOS UMA tag em comum
    return p.tags.some(function (tag) {
      return projeto.tags.includes(tag);
    });

  })
  .slice(0, 4); // limita a 4 itens

  // ------------------------------------------------------------
  // 🔹 CONTROLE DE SCROLL DO CARROSSEL
  // ------------------------------------------------------------

  function verificarScroll() {
    const container = containerRef.current;

    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // Atualiza estado das setas
    setPodeIrEsquerda(scrollLeft > 0);
    setPodeIrDireita(scrollLeft + clientWidth < scrollWidth - 1);
  }

  // Move o carrossel para a esquerda
  function scrollLeft() {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  }

  // Move o carrossel para a direita
  function scrollRight() {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  }

  // ------------------------------------------------------------
  // 🔹 RENDERIZAÇÃO
  // ------------------------------------------------------------

  return (
    <main className="detalhes">

      {/* 🔹 Área de topo (background/estética) */}
      <section className="detalhes-topo"></section>

      {/* 🔹 Conteúdo principal */}
      <section className="detalhes-conteudo">

        <div className="detalhes-container">

          {/* -------------------------------------------------- */}
          {/* 🔹 MÍDIA DINÂMICA DO PROJETO */}
          {/* -------------------------------------------------- */}
          <div className="detalhe-media">

            {/* Imagem */}
            {apresentacao.tipo === "imagem" && (
              <img
                src={apresentacao.arquivo}
                alt={projeto.titulo}
              />
            )}

            {/* Vídeo local */}
            {apresentacao.tipo === "video" && (
              <video controls style={{ width: "100%", maxHeight: 520 }}>
                <source src={apresentacao.arquivo} type="video/mp4" />
                Seu navegador não suporta o elemento de vídeo.
              </video>
            )}

            {/* YouTube */}
            {apresentacao.tipo === "youtube" && (
              <iframe
                title={projeto.titulo}
                src={apresentacao.url}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: 520 }}
              />
            )}

          </div>

          {/* -------------------------------------------------- */}
          {/* 🔹 INFORMAÇÕES DO PROJETO */}
          {/* -------------------------------------------------- */}
          <article className="detalhe-card">

            {/* Cabeçalho */}
            <div className="detalhe-cabecalho">

              <h1>{projeto.titulo}</h1>

              {/* Tags */}
              <div className="detalhe-tags">
                {projeto.tags.map((t) => (
                  <span key={t} className="card-tag">
                    {t}
                  </span>
                ))}
              </div>

            </div>

            {/* Descrição */}
            <div className="detalhe-info">
              <p>{projeto.info || projeto.descricao}</p>
            </div>

            {/* -------------------------------------------------- */}
            {/* 🔹 LINKS DE DISPONIBILIDADE */}
            {/* -------------------------------------------------- */}
            <div className="detalhe-links">

              {/* Renderização dinâmica baseada no JSON */}
              {projeto.disponibilidade && projeto.disponibilidade.map(function (item, index) {

                const config = iconesDisponibilidade[item.tipo];

                // Se não houver configuração para o tipo, ignora
                if (!config) {
                  return null;
                }

                return (
                  <a
                    key={index}
                    href={item.url || item.link}
                    className="detalhe-botao"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={config.icone} alt={config.label} />
                    {config.label}
                  </a>
                );

              })}

            </div>

          </article>

        </div>

        {/* -------------------------------------------------- */}
        {/* 🔹 PROJETOS RELACIONADOS */}
        {/* -------------------------------------------------- */}
        {relacionado.length > 0 && (
          <section className="relacionados">

            <div className="relacionados-container">

              <h2 className="relacionados-titulo">
                Projetos relacionados
              </h2>

              <div className="carousel-wrapper">

                {/* Botão esquerda */}
                {podeIrEsquerda && (
                  <button className="carousel-btn left" onClick={scrollLeft}>
                    ❮
                  </button>
                )}

                {/* Lista de cards */}
                <div ref={containerRef} className="cards-container">
                  {relacionado.map(function (p) {
                    return (
                      <Card
                        key={p.id}
                        projeto={p}
                        onClick={function (id) {
                          navigate(`/projeto/${id}`);
                        }}
                      />
                    );
                  })}
                </div>

                {/* Botão direita */}
                {podeIrDireita && (
                  <button className="carousel-btn right" onClick={scrollRight}>
                    ❯
                  </button>
                )}

              </div>

            </div>

          </section>
        )}

      </section>

    </main>
  );
}

export default Detalhes;